import { PenLine, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppDatePicker from "../../../components/ui/AppDatePicker";
import AppInputField from "../../../components/ui/AppInputField";
import { Modal } from "../../../components/ui/Modal";
import { MySelect } from "../../../components/ui/Select";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import ExpenseHeadComboInput from "../../finance/components/ExpenseHeadDropDownMenuInput";
import { Dispatch, Expense, FuelExpense } from "../@types";
import { useUpdateDispatchMutation } from "../dispatchApiSlice";

interface UpdateExpenseProps {
  dispatch: Dispatch;
  refresh: () => void;
}

const FUEL_EXPENSE_PREFIX = "fuel expense";

const UpdateExpense: React.FC<UpdateExpenseProps> = ({ dispatch, refresh }) => {
  const [openModal, setOpenModal] = useState(false);
  const [expenses, setExpenses] = useState<Expense[]>(dispatch.expenses || []);
  const [fuelExpenses, setFuelExpenses] = useState<FuelExpense>(
    dispatch.fuelExpense,
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();

  const [updateDispatch, { isLoading, isSuccess, reset }] =
    useUpdateDispatchMutation();

  // Separate fixed and other expenses
  const { fuelExpense, otherExpenses } = useMemo(() => {
    const fuel = expenses.find((e) =>
      e.description.toLowerCase().startsWith(FUEL_EXPENSE_PREFIX),
    );
    const others = expenses.filter(
      (e) => !e.description.toLowerCase().startsWith(FUEL_EXPENSE_PREFIX),
    );
    return { fuelExpense: fuel, otherExpenses: others };
  }, [expenses]);

  const handleUpdateFuelExpenses = <K extends keyof FuelExpense>(
    field: K,
    value: FuelExpense[K],
  ) => {
    setFuelExpenses((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle other expense updates
  const handleUpdateOtherExpense = useCallback(
    (
      index: number,
      field: "description" | "amount",
      value: string | number,
    ) => {
      const otherExpensesCopy = [...otherExpenses];
      otherExpensesCopy[index] = {
        ...otherExpensesCopy[index],
        [field]: value,
      };

      setExpenses((prev) => [
        ...prev.filter((e) =>
          e.description.toLowerCase().startsWith(FUEL_EXPENSE_PREFIX),
        ),
        ...otherExpensesCopy,
      ]);
    },
    [otherExpenses],
  );

  // Add new expense
  const handleAddExpense = useCallback(() => {
    const newExpense: Expense = {
      // id: `expense-${Date.now()}`,
      description: "",
      amount: 0,
    };
    setExpenses((prev) => [...prev, newExpense]);
  }, []);

  // Remove other expense
  const handleRemoveExpense = useCallback(
    (index: number) => {
      const expenseToRemove = otherExpenses[index];
      setExpenses((prev) => prev.filter((e) => e.id !== expenseToRemove.id));
    },
    [otherExpenses],
  );

  // Validate expenses
  const validateExpenses = useCallback((): string | null => {
    if (!fuelExpenses || fuelExpenses.amount <= 0) {
      return "Fuel expense amount is required";
    }

    for (const expense of otherExpenses) {
      if (!expense.description.trim()) {
        return "All expense descriptions are required";
      }
      if (expense.amount <= 0) {
        return "All expense amounts must be greater than 0";
      }
    }

    return null;
  }, [fuelExpenses, otherExpenses]);

  // Submit handler
  const handleSubmit = async () => {
    const validationError = validateExpenses();
    if (validationError) {
      toast.error({
        title: "Validation Error",
        message: validationError,
      });
      return;
    }

    try {
      await updateDispatch({
        id: dispatch.id,
        isCompleted: dispatch.isCompleted,
        incomes: dispatch.incomes,
        expenses: expenses,
        vehicleId: dispatch.vehicleId,
        fuelExpense: fuelExpenses,
        fromLocation: dispatch.fromLocation,
        toLocation: dispatch.toLocation,
        approximateDistance: dispatch.approximateDistance,
        crews: dispatch.crews,
        totalExpectedIncome: dispatch.totalExpectedIncome,
        totalIncomeEarned: dispatch.totalIncomeEarned,
        totalPassengers: dispatch.totalPassengers,
        perPassengerRate: dispatch.perPassengerRate,
      }).unwrap();

      toast.success({
        title: "Success",
        message: "Dispatch expenses updated successfully",
      });
      setShowSuccess(true);
    } catch (error) {
      const errorMessage =
        getErrorDetails(error).message ||
        "Failed to update dispatch expenses. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  // Close handlers
  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setExpenses(dispatch.expenses || []);
    reset();
  }, [dispatch.expenses, reset]);

  const handleCloseSuccess = useCallback(() => {
    setShowSuccess(false);
    setOpenModal(false);
    reset();
    refresh();
  }, [reset, refresh]);

  // Render success view
  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseSuccess}
          heading="Success"
          message="Expenses have been updated successfully."
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : "Update Expenses";

  return (
    <>
      <div
        onClick={() => setOpenModal(true)}
        className="flex items-end justify-end mt-8 cursor-pointer"
      >
        <div className="flex items-center gap-1 border border-secondary rounded-2xl px-2 py-1 text-[13px] text-secondary hover:bg-secondary/5 transition-colors">
          Edit <PenLine className="h-4 w-4" />
        </div>
      </div>

      <Modal
        isOpen={openModal}
        onClose={handleCloseModal}
        title={modalTitle}
        className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white px-2 pb-4 overflow-hidden"
      >
        {isSuccess ? (
          renderSuccessView()
        ) : (
          <div className="p-6">
            <div className="p-4 space-y-4 border border-gray-200 rounded-[10px]">
              {/* Fixed Expense Section */}
              <div className="space-y-4">
                <h3 className="text-[13px] font-semibold">Fixed Expense</h3>
                <div className="text-[13px] font-normal">Fuel Purchase</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <AppInputField
                    type="number"
                    label="Quantities"
                    value={fuelExpenses.quantity}
                    onChange={(e) =>
                      handleUpdateFuelExpenses(
                        "quantity",
                        e.target.valueAsNumber,
                      )
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter liters"
                  />
                  <AppInputField
                    type="number"
                    label="Price per unit"
                    value={fuelExpenses.pricePerUnit}
                    onChange={(e) =>
                      handleUpdateFuelExpenses(
                        "pricePerUnit",
                        e.target.valueAsNumber,
                      )
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter price per unit"
                  />
                  <AppInputField
                    type="number"
                    label="Amount"
                    value={fuelExpenses.amount}
                    onChange={(e) =>
                      handleUpdateFuelExpenses("amount", e.target.valueAsNumber)
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter amount"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* <AppInputField
                    type="text"
                    label="Fuel Type"
                    value={fuelExpenses.fuelType}
                    onChange={(e) =>
                      handleUpdateFuelExpenses("fuelType", e.target.value)
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter fuel type"
                  /> */}

                  <MySelect<string>
                    name={"fuel"}
                    id={"id"}
                    label="Fuel Type"
                    value={fuelExpenses.fuelType}
                    options={["Petrol", "Diesel"]}
                    getValue={(option: string) => option}
                    getName={(option: string) => option}
                    onChange={(option: string) =>
                      handleUpdateFuelExpenses("fuelType", option)
                    }
                    onValChange={(option: string) =>
                      handleUpdateFuelExpenses("fuelType", option)
                    }
                    placeholder={"Select fuel type"}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  />

                  <AppDatePicker
                    label="Fuel Date"
                    value={
                      fuelExpenses.fuelDate
                        ? new Date(fuelExpenses.fuelDate)
                        : null
                    }
                    onChange={(date) =>
                      handleUpdateFuelExpenses(
                        "fuelDate",
                        date ? date.toISOString().split("T")[0] : "",
                      )
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  />

                  <AppInputField
                    type="number"
                    label="Odometer reading"
                    value={fuelExpenses.odometerReading}
                    onChange={(e) =>
                      handleUpdateFuelExpenses(
                        "odometerReading",
                        e.target.valueAsNumber,
                      )
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter odometer reading"
                  />
                  <AppInputField
                    type="text"
                    label="Station name"
                    value={fuelExpenses.stationName}
                    onChange={(e) =>
                      handleUpdateFuelExpenses("stationName", e.target.value)
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter station name"
                  />
                </div>
              </div>

              {/* Other Expenses Section */}
              <div className="space-y-4">
                <h3 className="text-[13px] font-semibold">Other Expenses</h3>
                <div className="space-y-4 w-full">
                  {otherExpenses.length > 0 ? (
                    otherExpenses.map((expense, index) => (
                      <div key={expense.id} className="space-y-2 w-full">
                        <div className="flex items-center gap-2 w-full">
                          <div className="w-full">
                            {/* <AppInputField
                              label={`Description ${index + 1}`}
                              type="text"
                              value={expense.description}
                              onChange={(e) =>
                                handleUpdateOtherExpense(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                              placeholder="Enter description"
                            /> */}

                            <ExpenseHeadComboInput
                              label={`Description ${index + 1}`}
                              type="text"
                              value={expense.description}
                              onChange={(e) =>
                                handleUpdateOtherExpense(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                              placeholder="Enter description"
                            />
                          </div>
                          <div className="w-full">
                            <AppInputField
                              label={`Amount ${index + 1}`}
                              type="number"
                              value={expense.amount || ""}
                              onChange={(e) =>
                                handleUpdateOtherExpense(
                                  index,
                                  "amount",
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                              placeholder="Enter amount"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="w-[90px] bg-red-100 flex items-center justify-center h-[48px] rounded-[10px] mt-6">
                            <button
                              type="button"
                              onClick={() => handleRemoveExpense(index)}
                              className="text-red-500 hover:text-red-600 transition-colors p-1"
                              aria-label="Remove expense"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No other expenses added yet
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleAddExpense}
                    className="text-secondary font-semibold hover:text-primary transition-colors text-[13px]"
                  >
                    + Add Expense
                  </button>
                </div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex justify-start items-center gap-4 pt-4">
              <AppButton
                type="button"
                onClick={handleCloseModal}
                disabled={isLoading}
                className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-200 transition-colors text-[15px]"
              >
                Cancel
              </AppButton>

              <AppButton
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Updating..." : "Update"}
              </AppButton>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UpdateExpense;
